import { useMutation, useQuery, useQueryClient } from "react-query";

import axiosInstance from "../config/Axios";
import { useCustomToast } from "../pages/Contexts/ToastContext";
import { usePlanState } from "../utils/planState";

const getEmergencyStatus = async () => {
    const { data } = await axiosInstance.get("/emergency/status");
    return data?.data;
};

export const useEmergencyStatus = ({ refetchInterval = false } = {}) => {
    // Part of the shell (banner + button on every page): with no active package the
    // endpoint answers 402, and the no-package state must not fire protected calls.
    const hasPlan = usePlanState().status === 'active';
    const { isLoading, data, refetch } = useQuery(
        "getEmergencyStatus",
        getEmergencyStatus,
        {
            refetchInterval: hasPlan ? refetchInterval : false,
            staleTime: 30_000,
            enabled: hasPlan,
        }
    );
    return { isLoading, data, refetch };
};

export const useActivateEmergency = () => {
    const queryClient = useQueryClient();
    const { showToast } = useCustomToast();

    return useMutation(
        async ({ message }) => {
            const { data } = await axiosInstance.post("/emergency/activate", { message });
            return data?.data;
        },
        {
            onSuccess: () => {
                queryClient.invalidateQueries("getEmergencyStatus");
                showToast("success", "Emergency mode", "All screens switched to safe content.");
            },
            onError: (error) => {
                showToast("error", "Emergency mode", error?.response?.data?.message || "Failed to activate.");
            },
        }
    );
};

export const useDeactivateEmergency = () => {
    const queryClient = useQueryClient();
    const { showToast } = useCustomToast();

    return useMutation(
        async () => {
            const { data } = await axiosInstance.post("/emergency/deactivate");
            return data?.data;
        },
        {
            onSuccess: () => {
                queryClient.invalidateQueries("getEmergencyStatus");
                showToast("success", "Emergency mode", "Normal playback resumed.");
            },
            onError: (error) => {
                showToast("error", "Emergency mode", error?.response?.data?.message || "Failed to deactivate.");
            },
        }
    );
};

export const useUpdateEmergencySettings = () => {
    const queryClient = useQueryClient();
    const { showToast } = useCustomToast();

    return useMutation(
        async ({ message, logoFile, clearLogo }) => {
            const formData = new FormData();
            if (message !== undefined && message !== null) formData.append("message", message);
            if (logoFile) formData.append("logo", logoFile);
            if (clearLogo) formData.append("clear_logo", 1);

            const { data } = await axiosInstance.post("/emergency/settings", formData, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            return data?.data;
        },
        {
            onSuccess: () => {
                queryClient.invalidateQueries("getEmergencyStatus");
                showToast("success", "Emergency settings", "Updated.");
            },
            onError: (error) => {
                showToast("error", "Emergency settings", error?.response?.data?.message || "Failed to update.");
            },
        }
    );
};
